/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from "react";
import CommonModule from "../CommonModule";
import { LevelColDefs } from "../columnDefs";
import { apiRequest } from "../../../../common/common";
import { showNotification } from "../../../../common/Notification/ToastifyNotification";
import FilterPanel from "./FilterPanel";
import { LoadingPage } from "../../../../common/LoadingPage";
import { Box, Button, Dialog, DialogActions, DialogContent, Divider, Grid, Link, styled, Switch, Tooltip, Typography } from "@mui/material";
import { BuildingType, CurrencyJSON, getProjectSize } from "../../../../common/const";
import { ArrowBack, DeleteOutline, EditOutlined } from "@mui/icons-material";
import CreateModule from "../CreateModule";
import { useSelector } from "react-redux";
import ColumnPanel from "../../../../common/ColumnPanel";
import * as XLSX from 'xlsx';

const DemoTypo = styled(Typography)(({ theme }) => ({
  fontSize: 14
}));

const DemoTypo1 = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 'bold'
}));

const FixedBox = styled(Box)(({ theme }) => ({
  width: '110px'
}));

const LevelModule = ({
  projectId,
  projectData,
  building,
  setActiveTab,
  setLevel,
}) => {
  const { user, roles } = useSelector((state) => state.users);
  const isUser = roles.find((a) => a.id === user?.role_id);
  const [rowsPerPage, setRowPerPage] = useState(localStorage.getItem('levelPageSize') || 10);
  const [searchingText, setSearchText] = useState(null || localStorage.getItem('search'));
  const [gridApi, setGridApi] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [notes, setNotes] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [levelId, setLevelId] = useState(null);
  const [open, setOpen] = useState(false);
  const [levelData, setLevelData] = useState(null);
  const [countData, setCountData] = useState({});
  const [levelColumn, setLevelColumn] = useState(LevelColDefs);
  const [settingData, setSettingData] = useState([]);

  const onGridReady = useCallback((params) => {
    if (params?.api) {
      const { api, searchingText, filters } = params;
      if (filters) {
        localStorage.setItem('levelfilters', JSON.stringify(filters));
      }
      const filter = localStorage.getItem('levelfilters') ? JSON.parse(localStorage.getItem('levelfilters')) : filters;
      setGridApi(api);
      setDataLoading(true);
      const dataSource = {
        rowCount: null,
        getRows: async (params) => {
          const sortModel = params?.sortModel[0];
          const sortBy = {
            column: sortModel?.colId || '',
            order: sortModel?.sort || '',
          };
          if (projectId) {
            const url = [
              `/level?projectId=${projectId}`,
              building?.building_id ? `&buildingId=${building?.building_id}` : '',
              `pageNo=${Math.floor((params.startRow / rowsPerPage) + 1)}`,
              `pageSize=${rowsPerPage}`,
              filter?.isDeleted === 'yes' || filter?.isDeleted === 'no' ? `isDeleted=${filter?.isDeleted === 'yes' ? true : ''}` : `isDeleted=${building?.is_deleted ? true : ''}`,
              sortBy?.column ? `sortBy=${sortBy.column}` : '',
              sortBy?.order ? `sortOrder=${sortBy.order}` : '',
              searchingText || localStorage.getItem('search') ? `searchText=${encodeURIComponent(searchingText || localStorage.getItem('search'))}` : ''
            ].filter(Boolean).join('&');
            try {
              const response = await apiRequest('get', `${url}`);
              const { data, total_count, total_area, total_cost, total_level_count } = response.data;
              let lastRow = -1;
              if (data?.length <= total_count) {
                lastRow = total_count;
              }
              setCountData({
                total_area,
                total_quantity: total_level_count,
                total_cost
              });
              params.successCallback(data, lastRow);
              setDataLoading(false);
            } catch (error) {
              setDataLoading(false);
              showNotification(error?.response?.data.error || error.message, 'error');
            }
          }
        },
      };
      api.setGridOption("datasource", dataSource);
      setTimeout(() => {
        const node = api.getRenderedNodes();
        if (!node?.[0]?.displayed) {
          api.showNoRowsOverlay();
        }
      }, 5000);
    }
  }, [rowsPerPage, projectId, building?.building_id]);

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      onGridReady({
        api: gridApi,
      });
    }
    return () => {
      isMounted = false;
    };
  }, [rowsPerPage, projectId, building?.building_id]);

  useEffect(() => {
    if (levelColumn?.length > 0 && gridApi) {
      const columnState = gridApi?.getColumnState()?.map(state => {
        const columnConfig = levelColumn.find((c) => c.colId === state.colId || c.field === state.colId);
        if (columnConfig) {
          if (columnConfig.pinned !== state.pinned) {
            state.pinned = columnConfig.pinned ? columnConfig.pinned : null;
          }
        }

        return state;
      });
      gridApi?.applyColumnState({ state: columnState });
      gridApi?.refreshHeader();
    }
  }, [levelColumn]);


  const getData = async () => {
    try {
      setDataLoading(true)
      const response = await apiRequest('get', `/user-view?userId=${user?.user_id}&type=project-level&pageNo=1&pageSize=50`);
      const { data } = response.data;
      if (data?.length > 0) {
        if (data?.find((a) => a.is_pinned === true)?.columns) {
          setLevelColumn([...levelColumn.slice(0, 3), ...data?.find((a) => a.is_pinned === true)?.columns]);
        }
        setSettingData(data);
      } else {
        setSettingData([]);
      }
      setDataLoading(false);
    } catch (error) {
      setDataLoading(false);
      showNotification(error?.response?.data.error || error.message, 'error');
    }
  }

  useEffect(() => {
    if (user?.user_id) {
      getData();
    }
  }, [user?.user_id])

  const handleExportExcel = async () => {
    try {
      setDataLoading(true);
      const url = [
        `/level?projectId=${projectId}`,
        building?.building_id ? `buildingId=${building.building_id}` : '',
        `pageNo=1`, `pageSize=10000`,
      ].filter(Boolean).join('&');
      const response = await apiRequest('get', url);
      const { data } = response.data;
      const currencySymbol = CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol ?? '';
      const exportRows = (data || []).map((row) => ({
        'Level No': row.level_no ?? '--',
        'Level Name': row.level_name ?? '--',
        'Building Name': row.building?.building_name ?? '--',
        'Status': row.is_deleted === false ? 'Active' : 'InActive',
        'Active Rooms': row.active_room_count ?? 0,
        'Inactive Rooms': row.inactive_room_count ?? 0,
        'Combined Rooms': row.all_room_count ?? 0,
        'Total Area': row.total_area ? getProjectSize(projectData?.default_project_units, row.total_area) : '--',
        'Sub Total': `${currencySymbol} ${Intl.NumberFormat('en-IN').format(row.sub_total || 0)}`,
        'Total Cost': `${currencySymbol} ${Intl.NumberFormat('en-IN').format(row.level_total_amount || 0)}`,
        'Notes': row.notes ?? '--',
      }));
      const ws = XLSX.utils.json_to_sheet(exportRows);
      ws['!cols'] = Object.keys(exportRows[0] || {}).map((key) => ({
        wch: Math.max(key.length, ...exportRows.map((r) => String(r[key] ?? '').length)) + 2,
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Levels');
      XLSX.writeFile(wb, `Levels_${projectId}.xlsx`);
    } catch (error) {
      showNotification(error?.response?.data?.error || error.message, 'error');
    } finally {
      setDataLoading(false);
    }
  };

  const toolPanel = useMemo(() => [
    {
      id: 'custom1',
      labelDefault: 'Columns',
      iconKey: "columns",
      toolPanel: ColumnPanel,
      width: 450,
      toolPanelParams: {
        columns: levelColumn?.slice(3, 14),
        setColumns: (column) => {
          setLevelColumn([...levelColumn.slice(0, 3), ...column]);
        },
        settingData,
        fetchData: () => {
          if (user?.user_id) {
            getData();
          }
        },
        type: "project-level",
        defaultVisibleColumns: LevelColDefs?.slice(3, 14),
        columnOrder: levelColumn?.slice(3, 14)
      }
    },
    {
      id: 'custom2',
      iconKey: "filter",
      labelDefault: 'Filters',
      toolPanel: FilterPanel,
      width: 300,
      toolPanelParams: {
        applyFilter: (filters) => {
          setRowPerPage(localStorage.getItem('levelPageSize') || 10);
          onGridReady({
            api: gridApi,
            filters
          });
        }
      }
    }
  ], [gridApi, settingData, levelColumn]);

  const updatedColumns = useMemo(() => {
    let column = levelColumn;
    if (column.length > 0) {
      return column?.map((col) => {
        switch (col.field) {
          case '': {
            return {
              ...col,
              hide: isUser?.code === 'orgGuestUser'
            }
          }
          case 'action':
            return {
              ...col,
              hide: isUser?.code === 'orgGuestUser',
              cellRenderer: (params) => params?.data && (
                <Box style={{ display: 'flex', alignItems: 'center' }}>
                  {!params?.data?.is_deleted && (
                    <Tooltip title="Edit Level">
                      <EditOutlined
                        color="primary"
                        style={{ cursor: 'pointer', color: selectedData?.includes(params?.data?.level_id) ? '' : 'gray' }}
                        onClick={() => {
                          if (selectedData?.includes(params?.data?.level_id)) {
                            setOpen(true);
                            setLevelData(params?.data);
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title={params?.data?.is_deleted === false ? 'Active' : 'InActive'}>
                    <Switch
                      size="small"
                      disabled={!selectedData?.includes(params?.data?.level_id)}
                      checked={params?.data?.is_deleted === false}
                      onChange={async (e) => {
                        setDataLoading(true);
                        try {
                          const response = await apiRequest('patch', `/level/set-delete-status/${params?.data.level_id}`, {
                            isDeleted: e.target.checked === true ? false : true
                          });
                          if (response?.data) {
                            onGridReady({
                              api: gridApi
                            })
                            setDataLoading(false);
                            showNotification(response?.data?.message, 'success');
                          }
                        } catch (error) {
                          setDataLoading(false);
                          showNotification(error?.response?.data.error || error.message, 'error');
                        }
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Permanent Delete">
                    <DeleteOutline
                      color="secondary"
                      style={{ cursor: 'pointer', color: selectedData?.includes(params?.data?.level_id) ? '' : 'gray' }}
                      onClick={() => {
                        if (selectedData?.includes(params?.data?.level_id)) {
                          setDeleteOpen(true);
                          setLevelId(params?.data?.level_id);
                        }
                      }}
                    />
                  </Tooltip>
                </Box>
              ),
            }
          case 'level_name':
            return {
              ...col,
              flex: 1,
              minWidth: 150,
              cellRenderer: (params) => {
                if (params?.data && !params?.data.is_deleted) {
                  return (
                    <Link
                      style={{ textDecoration: 'none', cursor: 'pointer' }}
                      onClick={() => {
                        setActiveTab('Departments');
                        setLevel(params?.data);
                      }}
                    >

                      {params?.data?.level_name || '--'}
                    </Link>
                  )
                } else {
                  return (
                    <span style={{ color: 'red' }}>{params?.data?.level_name || '--'}</span>
                  )
                }
              },
            }
          case 'status':
            return {
              ...col,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {!params?.data?.is_deleted ? 'Active' : 'InActive'}
                </>
              ),
            }
          case 'building_name':
            return {
              ...col,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {params?.data?.building?.building_name || '--'}
                </>
              ),
            }
          case 'total_cost':
            return {
              ...col,
              flex: 1,
              minWidth: 175,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {`${CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol} ${Intl.NumberFormat('en-IN').format(params?.data?.level_total_amount || 0)}`}
                </>
              ),
            }
          case 'total_rooms':
            return {
              ...col,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {params?.data?.active_room_count || 0}
                </>
              ),
            }
          case 'total_inactive_rooms':
            return {
              ...col,
              flex: 1,
              minWidth: 120,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {params?.data?.inactive_room_count || 0}
                </>
              ),
            }
          case 'level_no':
            return {
              ...col,
              flex: 1,
              minWidth: 90,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data?.level_no ? (
                <span>{params?.data?.level_no}</span>
              ) : '--'
            }
          case 'subtotal':
            return {
              ...col,
              flex: 1,
              minWidth: 150,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {`${CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol} ${Intl.NumberFormat('en-IN').format(params?.data?.sub_total || 0)}`}
                </>
              ),
            }
          case 'total_combind_rooms':
            return {
              ...col,
              flex: 1,
              minWidth: 130,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {params?.data?.all_room_count || 0}
                </>
              ),
            }
          case 'total_area':
            return {
              ...col,
              flex: 1,
              minWidth: 220,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {params?.data?.total_area !== null && params?.data?.total_area ? getProjectSize(projectData?.default_project_units, (params?.data?.total_area)) : '--'}
                </>
              ),
            }
          case 'notes':
            return {
              ...col,
              flex: 1,
              minWidth: 110,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data?.notes ? (
                <Link
                  style={{ textDecoration: 'none', cursor: 'pointer' }}
                  onClick={() => {
                    setNotes(params?.data.notes);
                  }}
                >
                  View Notes
                </Link>
              ) : '--',
            }
          default:
            return col;
        }
      });
    }
    return column;
  }, [levelColumn, selectedData]);

  return (
    <>
      {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
      <Grid container>
        <Dialog open={deleteOpen} fullWidth maxWidth="xs">
          <DialogContent>Are you sure you want to permanently delete this one? It cannot be recovered.</DialogContent>
          <DialogActions>
            <Button size="small" variant="contained" color="secondary"
              onClick={() => {
                setLevelId(null);
                setDeleteOpen(false);
              }}
            >
              No
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={async () => {
                try {
                  setDataLoading(true);
                  let response = await apiRequest('delete', `/level/${levelId}`);
                  if (response?.data) {
                    setDataLoading(false);
                    showNotification(response?.data?.message, 'success');
                    setLevelId(null);
                    setDeleteOpen(false);
                    onGridReady({
                      api: gridApi
                    });
                  }
                } catch (error) {
                  setDataLoading(false);
                  showNotification(error?.response?.data.error || error.message, 'error');
                }
              }}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
        {open && (
          <CreateModule
            open={open}
            setOpen={setOpen}
            getData={() => onGridReady({
              api: gridApi
            })}
            innerLabel="Level"
            projectId={projectId}
            initialData={levelData}
          />
        )}
        <Grid item xs={12} sm={12} md={building || notes !== null ? 10 : 12} style={{ padding: '0px 8px 0px 8px' }}>
          <CommonModule
            label={"Levels"}
            innerLabel="Level"
            columnDefs={updatedColumns}
            onGridReady={onGridReady}
            rowsPerPage={rowsPerPage}
            setRowPerPage={setRowPerPage}
            searchingText={searchingText}
            setSearchText={setSearchText}
            toolPanel={toolPanel}
            projectId={projectId}
            gridApi={gridApi}
            building={building}
            onSelectionChanged={() => {
              if (gridApi) {
                const selectedNodes = gridApi.getSelectedNodes();
                setSelectedData(selectedNodes.map((node) => node.data.level_id));
              }
            }}
            projectData={projectData}
            selectionType="single"
            countData={countData}
            setSelectedData={setSelectedData}
            setColumns={setLevelColumn}
            onExport={handleExportExcel}
          />
        </Grid>
        {notes !== null && (
          <Grid item xs={12} sm={12} md={2} style={{ borderLeft: '1px solid lightgray' }}>
            <Box mt={1} mb={1} ml={1}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 5 }} mb={1}>
                <ArrowBack style={{ cursor: 'pointer', fontSize: 20 }} onClick={() => {
                  setNotes(null);
                }} />
                <DemoTypo1 variant="body2">Notes:</DemoTypo1>
              </Box>
              <Divider />
              <Box style={{ height: '57vh', overflow: 'auto' }} mt={1}>
                <DemoTypo variant="body2">{notes}</DemoTypo>
              </Box>
            </Box>
          </Grid>
        )}
        {(building !== null && notes === null) && (
          <Grid item xs={12} sm={12} md={2} style={{ borderLeft: '1px solid lightgray' }}>
            <Box mt={1} mb={1} ml={1}>
              <Box mt={1} style={{ display: 'flex', alignItems: 'center', gap: 5, }}>
                <FixedBox>
                  <DemoTypo1 variant="body2">Building Name:</DemoTypo1>
                </FixedBox>
                <DemoTypo variant="body2">{building?.building_name}</DemoTypo>
              </Box>
              <Box mt={1} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <FixedBox>
                  <DemoTypo1 variant="body2">Building Type:</DemoTypo1>
                </FixedBox>
                <DemoTypo variant="body2">{BuildingType.find((a) => a.value === building?.type)?.name || '--'}</DemoTypo>
              </Box>
              <Box mt={1} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <FixedBox>
                  <DemoTypo1 variant="body2">Building ID:</DemoTypo1>
                </FixedBox>
                <DemoTypo variant="body2">{building?.building_no || '--'}</DemoTypo>
              </Box>
              <Box mt={1} style={{ display: 'flex', gap: 5 }}>
                <FixedBox>
                  <DemoTypo1 variant="body2">Building Notes:</DemoTypo1>
                </FixedBox>
                <DemoTypo variant="body2">{building?.notes || '--'}</DemoTypo>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default LevelModule;